/**
 * Legible ink for the "This segment is empty" placeholder (findings F3.6 /
 * F11.3 / F15.7).
 *
 * The placeholder text was hardcoded `#666` in BOTH segment render paths
 * (`components/SegmentView/index.jsx` and `pages/Segment/index.jsx`). That is
 * fine over the default white panel (5.74:1) but the per-Segment environment
 * lets a creator paint the panel any color they like — over a dark solid such
 * as `#303030` the placeholder measured **2.30:1**, well under the WCAG AA
 * 4.5:1 floor for 16px text, and there is no contrast guard anywhere in the
 * wallpaper picker.
 *
 * This helper keeps the existing `#666` EXACTLY as-is whenever it still passes
 * AA (so nothing changes on the overwhelmingly common light backgrounds) and
 * only swaps in the higher-contrast ink/white pick when it does not. When the
 * effective background cannot be sampled statically — an image wallpaper, a
 * pattern, an unparseable custom string — it falls back to `#666` unchanged,
 * so the guard can never make an un-evaluable case worse.
 */

import { autoTextColor, effectiveBgHex, meetsContrastAA } from "./contrast";

/** The historical placeholder ink — preserved wherever it already passes AA. */
export const DEFAULT_EMPTY_STATE_INK = "#666666";

/**
 * Resolve the CSS background the empty-state text actually sits on, from the
 * per-Segment settings, mirroring how the two render paths paint it:
 *   currentBg 0 -> solid `segmentBgColor`
 *   currentBg 3 -> gradient string stored in `segmentBgColor`
 *   currentBg 1 -> pattern drawn over `wallpaperOverlayColor`
 *   currentBg 2 -> image wallpaper (not statically samplable)
 *
 * @param {{currentBg?: number, segmentBgColor?: string, wallpaperOverlayColor?: string}} settings
 * @returns {string|null} a CSS background value, or null when unsamplable
 */
export function resolveSegmentBackdrop(settings = {}) {
  const { currentBg, segmentBgColor, wallpaperOverlayColor } = settings || {};
  const bg = Number(currentBg);
  if (bg === 2) return null; // image wallpaper — cannot sample statically
  if (bg === 1) return wallpaperOverlayColor || null;
  // 0 (solid) and 3 (gradient) both live in segmentBgColor
  if (typeof segmentBgColor !== "string") return null;
  if (segmentBgColor === "transparent") return null;
  return segmentBgColor || null;
}

/**
 * Pick a legible ink for the empty-segment placeholder over a per-Segment
 * background.
 *
 * @param {{currentBg?: number, segmentBgColor?: string, wallpaperOverlayColor?: string}} settings
 * @returns {string} a hex color — `#666666` unless AA demands otherwise
 */
export function segmentEmptyStateInk(settings = {}) {
  const backdrop = resolveSegmentBackdrop(settings);
  const hex = effectiveBgHex(backdrop);
  // Un-samplable (image / pattern with no overlay / garbage) -> keep today's ink.
  if (!hex) return DEFAULT_EMPTY_STATE_INK;
  // Already legible -> byte-identical to today.
  if (meetsContrastAA(DEFAULT_EMPTY_STATE_INK, hex)) {
    return DEFAULT_EMPTY_STATE_INK;
  }
  // Otherwise take the higher-contrast of the app's ink/white pair.
  return autoTextColor(hex, { fallback: DEFAULT_EMPTY_STATE_INK });
}

export default segmentEmptyStateInk;
